import { useContext, useCallback, useMemo, useState, useEffect } from 'react'
import styled from '@emotion/styled'
import CircularProgress from '@mui/material/CircularProgress'
import uuid from 'uuid/v4'
import { CvsFieldMapperV2 } from '@components'
import { DependentMappings } from '@components/field-mapperV2/field-mapperV2'
import {
  getMappableFields,
  transformIntegrationFieldMappings,
  FieldMappingType,
  getMappingsToReset,
} from '@components/field-mapperV2/utils'
import {
  DefaultMappings,
  TemplateMappingOption,
} from '@components/field-mapperV2/utils/mapping-helpers'
import { useIntegrationAssignment } from '@global/hooks'
import { useCreativeClassifications } from '@global/hooks/queries/useCreativeClassifications'
import { api } from 'api'
import { templateCopyContext } from '../../../../context'

const FullWidth = styled.div`
  width: 100%;
`

interface FieldMappingManagerProps {
  integrationType: string
  integrationAssignment: any
  integrationTitle: string
  mappingType: FieldMappingType
  integrationFieldsFilter?: (field: any) => boolean
  mappingsDisabled?: boolean
  dependentMappings?: DependentMappings
  defaultMappings?: DefaultMappings
  dataType?: string
  assignmentMetaDataType?: string
  includeCreativeClassification?: boolean
}

export const FieldMappingManager = ({
  integrationType,
  integrationAssignment,
  integrationTitle,
  mappingType,
  integrationFieldsFilter,
  mappingsDisabled,
  dependentMappings,
  defaultMappings = [],
  dataType,
  assignmentMetaDataType,
  includeCreativeClassification,
}: FieldMappingManagerProps) => {
  const { template, onChange, onLoadIntegration } = useContext<any>(templateCopyContext)
  const [loadingFields, setLoadingFields] = useState<boolean>(true)
  const [fields, setFields] = useState([])
  const [customFields, setCustomFields] = useState([])
  const mappingsToReset = getMappingsToReset(fields)
  const { data: classifications = [] } = useCreativeClassifications()

  const { onIntegrationInit, onIntegrationLoad } = useIntegrationAssignment({
    integrationAssignment,
    onChange,
    onLoad: onLoadIntegration,
  })

  useEffect(() => {
    api
      .get(`/api/v1/integrations/${integrationType}/default-fields`)
      .then(resp => {
        setFields(resp.data)
        setLoadingFields(false)
      })
      .catch(() => {
        setLoadingFields(false)
      })
  }, [])

  useEffect(() => {
    if (!integrationAssignment.mapping_fields) {
      onIntegrationInit(mappingsToReset[dataType || 'default'], mappingsToReset)
    } else {
      onIntegrationLoad(mappingsToReset, 'mappingsToReset')
    }
  }, [fields])

  useEffect(() => {
    if (!integrationAssignment.connector_id) return
    api
      .get(`/api/v1/account/connector/${integrationAssignment.connector_id}/custom-fields`)
      .then(resp => {
        setCustomFields(resp.data)
      })
      .catch((err: unknown) => {
        console.log(err)
      })
  }, [integrationAssignment.connector_id])

  const onFieldMappingChange = useCallback(
    value => {
      onChange(old => ({
        ...old,
        [integrationAssignment.id]: {
          ...old[integrationAssignment.id],
          mapping_fields: value,
        },
      }))
    },
    [integrationAssignment, onChange],
  )

  const fieldOptions: TemplateMappingOption[] = useMemo(() => {
    const fields: TemplateMappingOption[] = []

    Object.values(template.templateFieldsById).forEach((field: any) =>
      fields.push({
        label: field.field?.name,
        value: field.field?.id?.toString(),
        governance: field.field?.governance_type || field?.governance_type,
        fieldSourceType: 'field',
      }),
    )

    if (includeCreativeClassification && mappingType == 'outbound') {
      template?.template?.creativeConfiguration?.forEach(i =>
        fields.push({
          label: classifications.find(({ id }) => id === i.categoryId)?.categoryText || '',
          value: i.templateClassificationId.toString() || uuid(),
          governance: 'text',
          fieldSourceType: 'template_classification',
        }),
      )
    }

    return fields
  }, [template])

  const mappings = transformIntegrationFieldMappings(
    integrationAssignment.mapping_fields,
    fieldOptions,
    defaultMappings || [],
    assignmentMetaDataType,
  )

  return (
    <FullWidth>
      {loadingFields ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <CircularProgress color="primary" />
        </div>
      ) : (
        <CvsFieldMapperV2
          mappingType={mappingType}
          integrationTitle={integrationTitle}
          integrationOptions={getMappableFields(
            fields,
            customFields,
            integrationFieldsFilter,
            dataType,
            integrationAssignment?.slug,
          )}
          templateOptions={fieldOptions}
          mappings={mappings}
          onSelect={value => {
            onFieldMappingChange(value)
          }}
          integration={integrationAssignment}
          mappingsDisabled={mappingsDisabled}
          dependentMappings={dependentMappings}
        />
      )}
    </FullWidth>
  )
}
